export interface IChipTreeData {
  id: number;
  name: string;
  parent_id: number;
  value: string | number;
  isActive?: boolean;
  data?: any;
}

export interface INormalizeTreeData<T> {
  byId: {
    [id: number]: T;
  };
  allIds: Array<number>;
}

export interface IChipsTree {
  chips: INormalizeTreeData<IChipTreeData>;
  tree: INormalizeTreeData<Array<number>>;
  multiple: boolean;
  toggleIsActive(chipId: number, value?: boolean | null): void;
  getActiveChips(): Array<IChipTreeData>;
  changeParentChips(parentId: number): void;
  changeAllIsActive(chips: Array<IChipTreeData>): void;
}

export default class ChipsTree implements IChipsTree {
  public chips: INormalizeTreeData<IChipTreeData>;
  public tree: INormalizeTreeData<Array<number>>;
  public multiple: boolean;

  constructor(chips: Array<IChipTreeData>, multiple: boolean) {
    this.chips = {
      byId: {},
      allIds: [],
    };
    this.tree = {
      byId: {},
      allIds: [],
    };
    this.multiple = multiple ?? false;

    if (chips?.length) {
      this.normalizeAllChips(chips);
    }
  }

  private normalizeAllChips(chips: Array<IChipTreeData>) {
    try {
      for (const chip of chips) {
        this.addChip(chip);
        this.sortTree(chip);
      }
    } catch (err) {
      console.error('normalizeAllChips', err);
    }
  }

  private addChip(chip: IChipTreeData) {
    this.chips.byId[chip.id] = { ...chip, isActive: false };
    this.chips.allIds.push(chip.id);
  }

  private sortTree(chip: IChipTreeData) {
    if (chip.id === chip.parent_id) {
      this.tree.byId[chip.id] = [];
      this.tree.allIds.push(chip.id);
      return;
    }

    if (this.tree.byId[chip.parent_id]) {
      this.tree.byId[chip.parent_id].push(chip.id);
    }
  }

  static formatToChipTreeData(
    data: Array<any>,
    idValue: string | null = null,
    isActive = false
  ): Array<IChipTreeData> | undefined {
    try {
      if (!data?.length) return;

      const chipsTreeData = [] as Array<IChipTreeData>;
      let tempData;

      for (const item of data) {
        if (typeof item === 'object') {
          tempData = {
            id: idValue ? item[idValue] : item.id,
            name: item.name || item.id?.toString(),
            parent_id: idValue ? item[idValue] : item.parent_id,
            value: (idValue ? item[idValue] : item.value)?.toString(),
          } as IChipTreeData;
        } else {
          tempData = {
            id: item,
            name: item,
            parent_id: item,
            value: item,
          };
        }

        tempData.isActive = isActive;

        if (item.data) {
          tempData.data = item.data;
        }

        chipsTreeData.push(tempData);
      }

      return chipsTreeData.map((c) => c);
    } catch (err) {
      console.error('formatToChipTreeData', err);
    }
  }

  public toggleIsActive(chipId: number, value: boolean | null = null): void {
    if (!chipId === undefined) return;

    if (!this.multiple) {
      for (const id of this.chips.allIds) {
        if (id === chipId) {
          this.chips.byId[id].isActive = value ?? !this.chips.byId[id].isActive;
          continue;
        }

        this.chips.byId[id].isActive = false;
      }

      return;
    }

    if (value !== null) {
      this.chips.byId[chipId].isActive = value;
      return;
    }

    this.chips.byId[chipId].isActive = !this.chips.byId[chipId].isActive;
  }

  public changeAllIsActive(chips: Array<IChipTreeData>): void {
    if (!chips?.length) return;

    for (const chip of chips) {
      if (this.chips.byId[chip.id]) {
        this.chips.byId[chip.id].isActive = true;
      }
    }
  }

  public getActiveChips(): Array<IChipTreeData> {
    const activeChips: Array<IChipTreeData> = [];

    for (const chipId of this.chips.allIds) {
      if (this.chips.byId[chipId].isActive) {
        activeChips.push(this.chips.byId[chipId]);
      }
    }
    return activeChips;
  }

  public changeParentChips(parentId: number): void {
    this.toggleIsActive(parentId);

    if (this.multiple) {
      if (this.tree.byId[parentId].length) {
        const parentIsActive = this.chips.byId[parentId].isActive;

        this.tree.byId[parentId].forEach((childId) => {
          this.toggleIsActive(childId, parentIsActive);
        });
      }
    }
  }
}
